import { Customer } from '@livekatsomo/models';
import { FileUploadProgess, UploadProgressFunction } from '@livekatsomo/types';
import { AssetImage } from '@livekatsomo/web/ui-components/assets';
import { PublicBadge, Ribbon } from '@livekatsomo/web/ui-components/layout';
import { NextLinkComposed } from '@livekatsomo/web/ui-components/link';
import { OverlayProgress } from '@livekatsomo/web/ui-components/overlay-progress';
import Upload from '@mui/icons-material/Upload';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

/**
 * Props for the CustomerCard component.
 */
export interface CustomerCardProps
  extends Pick<
    Customer,
    | 'name'
    | 'logo'
    | 'visibility'
    | 'slug'
    | 'description'
    | 'poster'
    | 'customerType'
  > {
  /**
   * Callback function to handle the edit button click event.
   */
  onEdit?: () => void;
  /**
   * Callback function to handle the logo upload event.
   * @param file - The file to upload.
   * @param onUploadProgressChange - Optional callback function to handle upload progress changes.
   * @param onUploadError - Optional callback function to handle upload errors.
   */
  onUploadLogo: (
    file: File,
    onUploadProgressChange?: UploadProgressFunction,
    onUploadError?: (error: Error) => void,
  ) => void;
}

/**
 * A card component that displays information about a customer, including their name, logo, description, and type.
 * Allows for uploading a new logo and editing the customer information.
 *
 * @param name - The name of the customer.
 * @param logo - The logo of the customer.
 * @param visibility - The visibility of the customer.
 * @param slug - The slug of the customer.
 * @param description - The description of the customer.
 * @param poster - The poster of the customer.
 * @param customerType - The type of the customer.
 * @param onUploadLogo - A function that handles uploading a new logo for the customer.
 * @param onEdit - A function that handles editing the customer information.
 */
export function CustomerCard({
  name,
  logo,
  visibility,
  slug,
  description,
  poster,
  customerType,
  onUploadLogo,
  onEdit,
}: CustomerCardProps) {
  const { t } = useTranslation();
  const [image, setFile] = useState<{
    preview: string;
    file: File;
  }>();
  const [uploadProgress, setProgress] = useState<FileUploadProgess>({});
  const [uploadError, setUploadError] = useState<Error>();

  const handleUploadFile = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) {
        return;
      }
      const file = acceptedFiles[0];
      setFile({
        preview: URL.createObjectURL(file),
        file,
      });
      onUploadLogo(file, setProgress, setUploadError);
    },
    [onUploadLogo],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop: handleUploadFile,
      noClick: true,
      accept: {
        'image/*': ['.jpeg', '.png', '.webp', '.svg'],
      },
      multiple: false,
    });

  return (
    <Card
      {...getRootProps()}
      sx={{ position: 'relative', overflow: 'visible' }}
      aria-labelledby="customer-card-header"
    >
      {customerType === 'shop' ? (
        <>
          <Ribbon position="top-right">{t('Shop')}</Ribbon>
          <CardMedia component={AssetImage} asset={poster} />
        </>
      ) : null}
      <CardContent>
        <CardHeader
          title={name}
          subheader={description || ''}
          id="customer-card-header"
        />
        <Box>{visibility === 'public' ? <PublicBadge /> : null}</Box>
        {uploadError ? (
          <Typography color="error">{uploadError.message}</Typography>
        ) : null}
      </CardContent>
      {image?.preview || logo ? (
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component={AssetImage}
            style={{
              objectFit: 'contain',
            }}
            height="140"
            asset={image?.preview || logo}
            alt={name}
          />
          {image &&
          uploadProgress &&
          uploadProgress[image.file.name]?.progress < 100 ? (
            <OverlayProgress
              uploadProgress={uploadProgress[image.file.name].progress}
            />
          ) : null}
        </Box>
      ) : null}
      <CardActions>
        <Button component="label" endIcon={<Upload />}>
          {t('Upload logo')}
          <input {...getInputProps()} />
        </Button>

        <Button component={NextLinkComposed} to={`/${slug}`}>
          {t('View')}
        </Button>

        {onEdit ? (
          <Button size="small" onClick={onEdit}>
            {t('Edit')}
          </Button>
        ) : null}
      </CardActions>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
          textAlign: 'center',
          display: isDragActive ? 'flex' : 'none',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: isDragReject
            ? (theme) => alpha(theme.palette.error.main, 0.8)
            : (theme) => alpha(theme.palette.grey[900], 0.8),
          color: 'common.white',
        }}
      >
        <Typography color="grey.100" variant="h4">
          {isDragReject ? t('Filetype not accepted') : t('Drop here')}
        </Typography>
        <Upload fontSize="large" />
      </Box>
    </Card>
  );
}

export default CustomerCard;
